import shortid from "shortid";
import { FetchMethod, FetchingKeys, Item } from "./types";
import { fireFetchingEvent, fireUpdateListEvent } from "./queuePool";

const intervalTime: number = 5 * 60 * 1000;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function Queue(queueName: string, fetchMethod: FetchMethod, initItems?: Item[]) {
  let items: Item[] = typeof initItems === "object" ? [...initItems] : [];
  const fetchingKeys: FetchingKeys = {};

  function setFetchingStatus(key: string, isFetching: boolean): void {
    if (isFetching) {
      fetchingKeys[key] = true;
    } else {
      delete fetchingKeys[key];
    }
    fireFetchingEvent(queueName, fetchingKeys);
  }

  function dequeue(key: string): void {
    items = items.filter((item) => item.key !== key);
    fireUpdateListEvent(queueName, items, "dequeue");
  }

  function getList(): Item[] {
    return items;
  }

  function logError(key: string, error: Error | string): void {
    items = items.map((item) => (item.key === key ? { ...item, error, errorDateTime: new Date() } : item));
    fireUpdateListEvent(queueName, items, "logError");
  }

  async function doFetch(item: Item): Promise<void> {
    if (fetchingKeys[item.key]) return;

    try {
      setFetchingStatus(item.key, true);
      await fetchMethod(item.data);
      dequeue(item.key);
    } catch (error) {
      logError(item.key, error);
    } finally {
      setFetchingStatus(item.key, false);
    }
  }

  function enqueue(data: Record<string, any>): boolean {
    const newitem: Item = { data, key: shortid.generate() };

    if (items.find((item) => item.key === newitem.key)) {
      newitem.key = shortid.generate();
      if (items.find((item) => item.key === newitem.key)) {
        return false;
      }
    }
    items = [...items, newitem];
    fireUpdateListEvent(queueName, items, "enqueue");
    doFetch(newitem);
    return true;
  }

  function isReadyToFetch(item: Item): boolean {
    if (item.errorDateTime) {
      const now = +new Date();
      const then = +new Date(item.errorDateTime);
      const diffMs = then - now;
      const diffInMinutes = Math.round(diffMs / 60000);
      return diffInMinutes > 5;
    }
    return true;
  }

  function fetchInterval(): void {
    items.forEach((item) => {
      if (isReadyToFetch(item)) {
        doFetch(item);
      }
    });
  }

  const intervalId = setInterval(fetchInterval, intervalTime);

  function removeInterval(): void {
    clearInterval(intervalId);
  }

  return Object.freeze({
    dequeue,
    enqueue,
    fetchingKeys,
    getList,
    removeInterval,
  });
}
